import { BookOpen, Target, ArrowLeftRight, Clock, Percent, Shield, Zap } from 'lucide-react';

export default function KnowledgeBase() {
  const sections = [
    {
      title: 'The Autonomous Allocation Engine',
      icon: Zap,
      content: `Thread Finance is built around an autonomous, event-driven allocation engine. When a deposit hits your bank account (like a paycheck), Plaid notifies our secure webhook instantly. The engine then wakes up, detects which income source the deposit came from, and executes your routing rules top-to-bottom. It physically distributes the money into your virtual "Funds" (buckets) before you even open the app.`,
    },
    {
      title: 'Priority Execution Order',
      icon: ArrowLeftRight,
      content: `Rules execute in a very strict, deterministic order to ensure you never over-draft your paycheck. 
      The engine executes your rules **strictly top-to-bottom** exactly as they appear on your Allocations screen. 
      - **Fixed Rules** deduct exactly the amount you specify.
      - **Percentage Rules** calculate their cut based on the *remaining unallocated balance* at that exact point in the list.
      - **Remainder Rules** scoop up 100% of whatever is left at the point they execute.
      You can use the Drag-and-Drop handles to reorder them to build powerful cascading strategies!`,
    },
    {
      title: 'Smart Funding (Due Days)',
      icon: Clock,
      content: `When creating a Fixed ($) rule, you can optionally set a "Due Day" (e.g., 15 for the 15th of the month). 
      The engine uses an advanced algorithm to look ahead. If the bill is due *before* your next paycheck, the engine will fully fund it from the current paycheck. If the bill isn't due until after your next paycheck, the engine will only fund *half* of it (or a quarter if you are paid weekly), allowing you to smoothly smooth out your expenses across multiple checks!`,
    },
    {
      title: 'Target Caps & Overdraft Protection',
      icon: Shield,
      content: `When you create a Fund, you can set a Target Amount (e.g., $1,000 for an Emergency Fund). The engine respects these caps perfectly. If a rule tries to put $500 into a fund that only has $100 of space left, the engine will cap the deposit at $100. 
      Furthermore, the engine has built-in overdraft protection. It will never allocate more money than what was actually deposited in the specific paycheck, ensuring your math always perfectly balances.`,
    },
    {
      title: 'Spillover Targets (Cascading)',
      icon: Target,
      content: `What happens when a bucket hits its Target Cap? By default, the excess money just stays "Unallocated" (Safe-to-Spend). But you can set up Spillover!
      When creating a rule, select a "Spillover to" target. If the rule hits a cap, the engine will automatically grab the excess money and push it into the Spillover target instead. You can chain these together to create complex, cascading waterfalls for your wealth.`,
    },
    {
      title: 'Percentage Splits',
      icon: Percent,
      content: `Percentage rules are extremely powerful because they are calculated against the **remaining balance** of the paycheck, not the gross amount. 
      This makes them perfect for splitting your leftover money. If you want to split all your leftover cash evenly between Savings and Guilt-Free Spending, simply add two 50% rules at the bottom of your allocations list!`,
    },
    {
      title: 'The Remainder Rule',
      icon: BookOpen,
      content: `The Remainder rule is a special "Catch-All". You can only have one Remainder rule per income source. It waits patiently for all other Fixed and Percentage rules to finish executing. Then, it scoops up 100% of the final unallocated balance and drops it into the assigned bucket. It ensures exactly $0.00 is left unallocated.`,
    }
  ];

  return (
    <div className="animate-fade-in" style={{ paddingBottom: '2rem' }}>
      <header style={{ marginBottom: '2rem' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
          <BookOpen className="text-primary" size={28} /> Knowledge Base
        </h2>
        <p className="text-muted" style={{ margin: 0, fontSize: '1.1rem' }}>
          Master the allocation engine and automate your financial life.
        </p>
      </header>

      <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {sections.map((sec, idx) => (
          <div key={idx} className="glass" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '1rem' }}>
              <div style={{ background: 'rgba(59, 130, 246, 0.1)', padding: '0.5rem', borderRadius: '8px', color: 'var(--primary)' }}>
                <sec.icon size={20} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{sec.title}</h3>
            </div>
            <div style={{ lineHeight: 1.6, color: 'var(--text-muted)', fontSize: '0.95rem' }} dangerouslySetInnerHTML={{ __html: sec.content.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<strong style="color:var(--text)">$1</strong>') }} />
          </div>
        ))}
      </div>
    </div>
  );
}
